import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import {
  resetFilters,
  selectActiveFilters,
  setMultipleFilters,
} from '../containers/ReviewsPage/components/ReviewsFilterDrawer/ReviewsFilterDrawer.duck';
import { fetchReviews, selectCurrentPage } from '../containers/ReviewsPage/ReviewsPage.duck';
import { addInchesToHeight } from '../util/string';

const FILTER_MAPPINGS = {
  pub_ClothingSize: value => ({
    size: value,
    sizeType: 'clothing',
    pub_categoryLevel1: 'clothing',
  }),
  pub_ShoeSize: value => ({
    size: value,
    sizeType: 'shoes',
    pub_categoryLevel1: 'shoes',
  }),
  pub_height: value => ({
    pub_height: addInchesToHeight(value),
  }),
  pub_brandSlug: value => ({ pub_brandSlug: value }),
  brandName: value => ({ brandName: value }),
  pub_categoryLevel1: value => ({ pub_categoryLevel1: value }),
};

function parseUrlToFilters(searchParams) {
  const params = Object.fromEntries(new URLSearchParams(searchParams).entries());
  const filters = {};

  ['pub_ClothingSize', 'pub_ShoeSize', 'pub_height', 'pub_brandSlug', 'brandName'].forEach(key => {
    if (params[key] && FILTER_MAPPINGS[key]) {
      Object.assign(filters, FILTER_MAPPINGS[key](params[key]));
    }
  });

  if (params.pub_categoryLevel1 && !filters.sizeType) {
    Object.assign(filters, FILTER_MAPPINGS.pub_categoryLevel1(params.pub_categoryLevel1));
  }

  return filters;
}

export function useUrlFilterSync() {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const isSyncingRef = useRef(false);

  const currentPage = useSelector(selectCurrentPage);
  const filters = useSelector(selectActiveFilters);

  const searchReviews = useCallback(
    payload => {
      dispatch(fetchReviews(payload));
    },
    [dispatch]
  );

  const setFilters = useCallback(
    params => {
      dispatch(setMultipleFilters(params));
    },
    [dispatch]
  );

  const clearFilters = useCallback(() => {
    dispatch(resetFilters());
  }, [dispatch]);

  // URL → Filters
  useEffect(() => {
    isSyncingRef.current = true;

    const params = new URLSearchParams(location.search);
    const initialFilters = { page: currentPage, ...Object.fromEntries(params.entries()) };
    const drawerFilters = parseUrlToFilters(location.search);

    setFilters(drawerFilters);
    searchReviews(initialFilters);

    requestAnimationFrame(() => {
      isSyncingRef.current = false;
    });

    return () => clearFilters();
  }, [location.search, currentPage]);

  // Filters → URL
  useEffect(() => {
    if (isSyncingRef.current || Object.keys(filters).length === 0) return;

    const queryString = new URLSearchParams(filters).toString();
    if (queryString !== location.search.slice(1)) {
      history.replace(`/reviews?${queryString}`);
    }
  }, [filters, history, location.search]);
}
